/**
 * @copyright Chianyu Inc., 2018 and onwards
 * @author Wang Chao
 * @date  2018/9/6 16:33
 * @des   文件类型判断工具类
 */

/**
 * 将文件字节数转换成带单位文字如：1024B => 1K
 * @param byteSize 字节数
 * @returns {string}
 */
export function convertFileSize (byteSize) {
  if (!byteSize) return ''
  let num = 1024.00 // byte
  if (byteSize < num) return byteSize + 'B' // B
  if (byteSize < Math.pow(num, 2)) return (byteSize / num).toFixed(2) + 'K' // K
  if (byteSize < Math.pow(num, 3)) return (byteSize / Math.pow(num, 2)).toFixed(2) + 'M' // M
  if (byteSize < Math.pow(num, 4)) return (byteSize / Math.pow(num, 3)).toFixed(2) + 'G' // G
  return (byteSize / Math.pow(num, 4)).toFixed(2) + 'T' // T
}

/**
 * 根据文件路径获取文件名
 * @param path
 */
export function getFileName (path) {
  if (path) {
    let name = path.trim()
    if (name.length > 0) {
      let index = name.lastIndexOf('/')
      if (index < 0) {
        index = name.lastIndexOf('\\')
      }
      if (index < 0) {
        return name
      }
      return name.substr(index + 1)
    }
  }
  return null
}

/**
 * 根据文件名获取文件扩展名
 * @param fileName
 */
export function getFileExt (fileName) {
  if (fileName) {
    let name = fileName.trim()
    if (name.length > 0) {
      let index = name.lastIndexOf('.')
      if (index > -1) {
        let ext = name.substr(index + 1)
        return ext.toLowerCase()
      }
    }
  }
  return null
}

/**
 * 是否是支持转换成pdf的文件
 * @param fileName
 * @returns {boolean}
 */
export function isPdfConvertibleFile (fileName) {
  let fileExt = getFileExt(fileName)
  if (fileExt) {
    let convertibleType = ['txt', 'c', 'java', 'conf', 'cpp', 'h', 'prop', 'rc', 'sh', 'log', 'xml', 'json', 'kt', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx']
    return convertibleType.indexOf(fileExt) > -1
  }
  return false
}

/**
 * 根据文件扩展名判断是否是支持打开操作的图片
 * @param fileExt
 */
export function isCanOpenImg (fileExt) {
  let imgExt = ['jpg', 'jpeg', 'png', 'bmp', 'gif', 'ico']
  for (let index in imgExt) {
    if (fileExt === imgExt[index]) {
      return true
    }
  }
  return false
}

/**
 * 根据拓展名判断是否是图片类型的文件
 * @param fileExt
 * @returns {boolean}
 */
export function isImage (fileExt) {
  let imgExt = ['jpg', 'jpeg', 'png', 'bmp', 'gif', 'ico', 'tiff', 'fpx', 'xwd', 'jpe']
  return imgExt.indexOf(fileExt) > -1
}

/**
 *
 * 根据文件扩展名判断是否是支持播放的语音
 * @param fileExt
 * @returns {boolean}
 */
export function isCanPlayVoice (fileExt) {
  let voiceExt = ['arm', 'wav', 'awb']
  for (let index in voiceExt) {
    if (fileExt === voiceExt[index]) {
      return true
    }
  }
  return false
}

/**
 * 根据拓展名判断是否是视频类型的文件
 * @param fileExt 文件后缀
 * @returns {boolean}
 */
export function isVideo (fileExt) {
  let videoType = ['avi', 'wmv', 'mkv', 'mp4', 'mov', 'rm', '3gp', 'flv', 'mpg', 'rmvb',]
  return videoType.indexOf(fileExt) > -1
}

/**
 * 根据文件名判断是否是APP内打开相机拍摄的视频
 * @param fileName 文件名
 * @returns {boolean}
 */
export function isShootVideo (fileName) {
  return fileName.endsWith('.chianyu.mp4')
}

/**
 * 判断web端是否可以打开的视频格式
 * @param fileExt 文件后缀
 * @returns {boolean}
 */
export function isCanPlayVideoForWeb (fileExt) {
  let videoType = ['mp4',]
  return videoType.indexOf(fileExt) > -1
}

/**
 * 根据拓展名判断是否是语音的文件
 * @param fileName 文件名
 * @returns {boolean}
 */
export function isVoice (fileName) {
  return fileName.endsWith('.chianyu.amr')
}

/**
 * 根据拓展名判断是否是Excel的文件
 * @param fileName 文件名
 * @returns {boolean}
 */
export function isExcel (fileName) {
  return fileName.endsWith('.xls') || fileName.endsWith('.xlsx')
}

/**
 * 根据拓展名判断是否是Pdf的文件
 * @param fileName 文件名
 * @returns {boolean}
 */
export function isPdf (fileName) {
  return fileName.endsWith('.pdf')
}

/**
 * 根据拓展名判断是否是Ppt的文件
 * @param fileName 文件名
 * @returns {boolean}
 */
export function isPpt (fileName) {
  return fileName.endsWith('.ppt')
}

/**
 * 根据拓展名判断是否是txt的文件
 * @param fileName 文件名
 * @returns {boolean}
 */
export function isTxt (fileName) {
  return fileName.endsWith('.txt')
}

/**
 * 根据拓展名判断是否是word的文件
 * @param fileName 文件名
 * @returns {boolean}
 */
export function isWord (fileName) {
  return fileName.endsWith('.doc') || fileName.endsWith('.docx')
}

/**
 * 根据拓展名判断是否是zip的文件
 * @param fileName 文件名
 * @returns {boolean}
 */
export function isZip (fileName) {
  return fileName.endsWith('.zip')
}

/**
 * 根据拓展名获取文件本地图标名称
 * @param fileName 文件名
 * @returns {boolean}
 */
export function getFileLocalIconName (fileName) {
  let fileExt = getFileExt(fileName)
  if (isImage(fileExt)) {
    return 'el_msg_file_image.png'
  } else if (isExcel(fileName)) {
    return 'el_msg_file_excel.png'
  } else if (isPdf(fileName)) {
    return 'el_msg_file_pdf.png'
  } else if (isPpt(fileName)) {
    return 'el_msg_file_ppt.png'
  } else if (isTxt(fileName)) {
    return 'el_msg_file_txt.png'
  } else if (isWord(fileName)) {
    return 'el_msg_file_word.png'
  } else if (isZip(fileName)) {
    return 'el_msg_file_zip.png'
  } else if (isVideo(fileExt)) {
    return 'el_msg_file_video.png'
  }
  return 'el_msg_file_unknown.png'
}



// WEBPACK FOOTER //
// ./src/base/util/fileutil.js